import {
  Component,
  computed,
  inject,
  input, ChangeDetectionStrategy} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { IssueTypeStore } from '../../../stores/issue-type.store';
import { ElementTypeEnum } from 'utils/interfaces/form.util';
import { FileUploadService } from '../../../services/file-upload.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  downloadTemplate,
  generateExcel,
  uniqueSortedEmails,
} from '../../../shared/functions/generate-excel';
import { ModuleStore } from '../../../stores/module.store';
import { WorkflowStateStore } from '../../../stores/workflow-state.store';
import { WorkflowState } from 'utils/interfaces/api-dtos.util';
import { UserService } from '../../../services/user.service';
import { Endpoint } from 'utils/enums/endpoint.util';
import { firstValueFrom } from 'rxjs';
import { UserWithTenantAndOrganization } from 'utils/interfaces/prisma-combined-types.util';

@Component({
    selector: 'gcp-workspace-import-issues',
    imports: [MatButton, MatIcon],
    templateUrl: './import-issues.component.html',
    styleUrl: './import-issues.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportIssuesComponent {
  issueTypeId = input<string | null>(null);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly moduleStore = inject(ModuleStore);
  private readonly workflowStateStore = inject(WorkflowStateStore);
  private readonly userService = inject(UserService);

  issueTypeStore = inject(IssueTypeStore);

  issueType = computed(
    () => this.issueTypeStore.entityMap()[this.issueTypeId() ?? '']
  );

  issueTypeHeaders = computed(
    () => this.issueType()?.formData.elements.map(({ name }) => name) ?? []
  );

  issueTypeHeaderToTypeMap = computed(() => {
    const map: Map<string, ElementTypeEnum> = new Map();
    for (const element of this.issueType().formData.elements) {
      map.set(element.name, element.type);
    }
    return map;
  });

  async uploadIssue(event: Event) {
    const files = (<HTMLInputElement>event.target).files;
    if (!files || files.length === 0) {
      return;
    }
    const uploadFile = await this.fileUploadService.uploadIssue(
      files[0],
      this.issueType().id
    );
    uploadFile.subscribe(() =>
      this.snackBar.open('Issue is being imported', '', { duration: 4000, panelClass: ['snackbar-info'] })
    );
  }

  async downloadExcel() {
    const issueType = this.issueType();
    if (!issueType?.formData?.elements) {
      return;
    }

    const workflowTypeIds = [
      ...new Set(
        issueType.formData.elements
          .filter((el) => el.type === ElementTypeEnum.WORKFLOW)
          .map((el) =>
            'workflowTypeId' in el && el.workflowTypeId ? el.workflowTypeId : ''
          )
          .filter((id) => id.length > 0)
      ),
    ];

    if (workflowTypeIds.length > 0) {
      const freshStates = await firstValueFrom(
        this.workflowStateStore.loadManyByType<WorkflowState[]>(
          workflowTypeIds,
          Endpoint.WORKFLOW_TYPE
        )
      );
      this.workflowStateStore.reconcileForWorkflowTypes(
        freshStates,
        workflowTypeIds
      );
    }

    const stateMap = this.workflowStateStore.workflowTypeIdToWorkflowStatesMap();
    const workflowLabelsByWorkflowTypeId = new Map<string, readonly string[]>();
    for (const wtId of workflowTypeIds) {
      const states = stateMap.get(wtId) ?? [];
      workflowLabelsByWorkflowTypeId.set(
        wtId,
        [...states]
          .sort((a, b) => a.position - b.position)
          .map((s) => s.label)
      );
    }

    const needsOrgUserEmails = issueType.formData.elements.some((el) => {
      if (el.type === ElementTypeEnum.PEOPLE_PICKER) {
        const users =
          'users' in el && Array.isArray(el.users) ? el.users : [];
        return !users.some((u) => u?.email && String(u.email).length > 0);
      }
      if (el.type === ElementTypeEnum.WATCHER) {
        const watchers =
          'watchers' in el && Array.isArray(el.watchers) ? el.watchers : [];
        return !watchers.some((w) => w?.email && String(w.email).length > 0);
      }
      return false;
    });

    let organizationUserEmails: readonly string[] | undefined;
    if (needsOrgUserEmails) {
      const raw = await firstValueFrom(
        this.userService.fetchUserByOrganization()
      );
      const users: UserWithTenantAndOrganization[] = Array.isArray(raw)
        ? raw
        : raw
          ? [raw as UserWithTenantAndOrganization]
          : [];
      organizationUserEmails = uniqueSortedEmails(
        users.map((u) => u.email).filter((e): e is string => !!e)
      );
    }

    const buffer = await generateExcel(
      issueType,
      workflowLabelsByWorkflowTypeId,
      organizationUserEmails
    );
    const moduleName =
      this.moduleStore.entityMap()[issueType.moduleId]?.name ?? '';
    const issueTypeName = issueType.name ?? '';
    let fileName: string;
    if (moduleName && issueTypeName) {
      fileName = `${moduleName} - ${issueTypeName}.xlsx`;
    } else if (issueTypeName) {
      fileName = `${issueTypeName}.xlsx`;
    } else {
      fileName = 'Template Document.xlsx';
    }
    downloadTemplate(buffer, fileName);
  }
}
